import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';

export interface XpSidebarItem {
	path: string;
	label: string;
	icon: string;
	badge?: number;
	badgeLabel?: string;
	testId?: string;
	children?: XpSidebarItem[];
}

@Component({
	selector: 'xp-sidebar',
	standalone: true,
	imports: [CommonModule, RouterLink],
	templateUrl: './xp-sidebar.component.html',
	styleUrls: ['./xp-sidebar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpSidebarComponent implements AfterViewInit {
	items = input<XpSidebarItem[]>([]);
	routePrefix = input.required<string[]>();
	logoSrc = input('');
	logoAlt = input('');
	darkMode = input(false);
	showAppearanceToggle = input(false);
	showFooter = input(true);
	userName = input('');
	userEmail = input('');
	darkModeChange = output<boolean>();
	logout = output<void>();
	leafSelected = output<void>();

	readonly collapsed = signal(false);
	readonly visible = signal(false);
	readonly userInitial = computed(() => this.userName().charAt(0));

	private readonly router = inject(Router);
	private readonly url = toSignal(
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => this.router.url)
		),
		{ initialValue: this.router.url }
	);
	private readonly currentPath = computed(() => this.url().split('?')[0].replace(/\/+$/, ''));
	private readonly openGroups = signal<ReadonlySet<string>>(new Set());

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.visible.set(true);
			this.openGroups.update(open => new Set([...open, ...this.activeGroupPaths(this.items())]));
		});
	}

	hasChildren(item: XpSidebarItem): boolean {
		return !!item.children?.length;
	}

	isActive(item: XpSidebarItem): boolean {
		return this.currentPath() === this.fullPath(item.path) || this.isChildActive(item);
	}

	isOpen(item: XpSidebarItem): boolean {
		return this.openGroups().has(item.path);
	}

	leafCommands(item: XpSidebarItem): string[] {
		return ['/', ...this.routePrefix(), ...item.path.split('/').filter(Boolean)];
	}

	linkClasses(item: XpSidebarItem, level: number): Record<string, boolean> {
		const link = level === 0 ? 'xp-sidebar__link' : 'xp-sidebar__sub-link';
		return {
			[link]: true,
			[`${link}--group`]: this.hasChildren(item),
			[`${link}--active`]: this.isActive(item)
		};
	}

	toggleCollapsed(): void {
		this.collapsed.update(collapsed => !collapsed);
	}

	toggleGroup(item: XpSidebarItem): void {
		this.openGroups.update(open => {
			const next = new Set(open);
			if (!next.delete(item.path)) {
				next.add(item.path);
			}
			return next;
		});
	}

	private isChildActive(item: XpSidebarItem): boolean {
		return item.children?.some(child => this.isActive(child)) ?? false;
	}

	private fullPath(itemPath: string): string {
		return `/${[...this.routePrefix(), ...itemPath.split('/').filter(Boolean)].join('/')}`;
	}

	private activeGroupPaths(items: XpSidebarItem[]): string[] {
		return items.flatMap(item => [
			...(this.hasChildren(item) && this.isChildActive(item) ? [item.path] : []),
			...this.activeGroupPaths(item.children ?? [])
		]);
	}
}
